import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, ShieldCheck, User } from "lucide-react";
import api, { inr, formatApiError } from "./api";
import { PageHeader, inputCls } from "./components";
import { EASE } from "@/components/Motion";

export default function ProfilePage() {
  const { user, refreshUser } = useOutletContext();
  const [name, setName] = useState(user?.name || "");
  const [savingName, setSavingName] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [savingPw, setSavingPw] = useState(false);

  const saveName = async (e) => {
    e.preventDefault();
    setSavingName(true);
    try {
      await api.put("/profile", { name });
      await refreshUser();
      toast.success("Profile updated");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingName(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    setSavingPw(true);
    try {
      await api.post("/profile/password", pw);
      setPw({ current_password: "", new_password: "" });
      toast.success("Password changed", { description: "Use it on your next sign in." });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingPw(false);
    }
  };

  return (
    <div data-testid="profile-page">
      <PageHeader title="Profile" sub="Your identity inside the empire." />

      <div className="grid gap-6 lg:grid-cols-2">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE }}
          className="space-y-6"
        >
          <div className="rounded-3xl border border-gold/20 bg-gradient-to-br from-gold/[0.08] to-transparent p-8" data-testid="profile-card">
            <div className="flex items-center gap-5">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-goldlight to-[#996515] text-2xl font-bold text-black">
                {user?.name?.[0]?.toUpperCase() || <User size={24} />}
              </div>
              <div>
                <div className="text-xl font-bold text-neutral-50">{user?.name}</div>
                <div className="mt-0.5 text-sm text-neutral-400">{user?.email}</div>
                <div className="mt-1 font-mono text-[10px] tracking-[0.2em] text-gold/70">
                  {user?.role?.toUpperCase()} · MEMBER SINCE {user ? new Date(user.created_at).toLocaleDateString("en-IN", { month: "short", year: "numeric" }).toUpperCase() : ""}
                </div>
              </div>
            </div>
            <div className="mt-6 flex items-center justify-between rounded-xl border border-white/10 bg-black/20 px-5 py-4">
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">Wallet balance</span>
              <span className="text-xl font-bold text-goldlight" data-testid="profile-balance">{inr(user?.balance ?? 0)}</span>
            </div>
          </div>

          <form onSubmit={saveName} className="rounded-2xl border border-white/10 bg-panel/80 p-6 sm:p-8" data-testid="profile-name-form">
            <h3 className="text-lg font-semibold text-neutral-100">Display name</h3>
            <div className="mt-4 flex gap-3">
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                minLength={2}
                className={inputCls}
                data-testid="profile-name-input"
              />
              <button
                type="submit"
                disabled={savingName || name === user?.name}
                data-testid="profile-save-button"
                className="shrink-0 rounded-xl border border-gold/40 px-6 text-sm font-semibold text-goldlight transition-all duration-300 hover:bg-gold hover:text-black disabled:opacity-40"
              >
                {savingName ? <Loader2 size={15} className="animate-spin" /> : "Save"}
              </button>
            </div>
          </form>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: EASE, delay: 0.12 }}
          onSubmit={savePassword}
          className="h-fit rounded-2xl border border-white/10 bg-panel/80 p-6 sm:p-8"
          data-testid="password-form"
        >
          <h3 className="flex items-center gap-2 text-lg font-semibold text-neutral-100">
            <ShieldCheck size={17} className="text-gold" /> Change password
          </h3>
          <div className="mt-6 space-y-4">
            <input
              type="password"
              value={pw.current_password}
              onChange={(e) => setPw({ ...pw, current_password: e.target.value })}
              placeholder="Current password"
              required
              className={inputCls}
              data-testid="password-current-input"
            />
            <input
              type="password"
              value={pw.new_password}
              onChange={(e) => setPw({ ...pw, new_password: e.target.value })}
              placeholder="New password (min. 8 characters)"
              required
              minLength={8}
              className={inputCls}
              data-testid="password-new-input"
            />
            <button
              type="submit"
              disabled={savingPw}
              data-testid="password-save-button"
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-goldlight via-gold to-golddark py-3.5 text-sm font-bold tracking-[0.12em] text-black transition-shadow duration-300 hover:shadow-[0_8px_30px_rgb(var(--gold)_/_0.3)] disabled:opacity-50"
            >
              {savingPw && <Loader2 size={15} className="animate-spin" />}
              UPDATE PASSWORD
            </button>
          </div>
        </motion.form>
      </div>
    </div>
  );
}
